import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { ApiError, errorMessage, fieldErrors } from "../../shared/api/errors.ts";
import {
  DEVICE_NAME_MAX_LENGTH,
  devicesQueryKey,
  JUST_CLAIMED_STATE,
  setActiveDeviceId,
} from "../../shared/devices/devices.ts";
import { useApiClient } from "../../shared/session/SessionProvider.tsx";
import { ArtEva } from "../../shared/ui/ArtEva/ArtEva.tsx";
import { Button, ButtonLink } from "../../shared/ui/Button/Button.tsx";
import { Input } from "../../shared/ui/Input/Input.tsx";
import styles from "./ClaimPage.module.css";

const DEFAULT_NAME = "Моя E-va";

type Errors = { name?: string; form?: string };

/**
 * Подтверждение привязки по ссылке из QR (`/claim?token=…`, ADR-005): название и «Подключить».
 * Каркас — 06.02 / DEVICE (Figma 953:12871): отдельного макета у подтверждения нет.
 */
export function ClaimPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token")?.trim() ?? "";
  return (
    <main className={styles.page}>
      <h1 className={styles.title}>Подключение устройства</h1>
      <div className={styles.body}>
        <ArtEva width={212} className={styles.art} />
        {token ? <ClaimForm token={token} /> : <MissingToken />}
      </div>
    </main>
  );
}

function ClaimForm({ token }: { token: string }) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState(DEFAULT_NAME);
  const [errors, setErrors] = useState<Errors>({});
  const mutation = useMutation({
    mutationFn: async (deviceName: string) => {
      const { data, error, response } = await client.POST("/v1/devices/claim", {
        body: { claimToken: token, name: deviceName },
      });
      if (!data) throw ApiError.from(response, error);
      return data.device;
    },
    onSuccess: async (device) => {
      setActiveDeviceId(device.id);
      await queryClient.invalidateQueries({ queryKey: devicesQueryKey });
      // replace: токен уже погашен, «назад» не должен возвращать на подтверждение.
      await navigate("/feed", { replace: true, state: JUST_CLAIMED_STATE });
    },
    onError: (error) => setErrors(errorsFrom(error)),
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setErrors({ name: "Введите название" });
      return;
    }
    mutation.mutate(trimmed);
  }

  return (
    <form className={styles.form} onSubmit={submit} noValidate>
      <div className={styles.text}>
        <h2 className={styles.heading}>Подключить эту E-va?</h2>
        <p className={styles.description}>Вы станете её владельцем и сможете отправлять на неё сообщения.</p>
      </div>
      <Input
        label="Название"
        name="name"
        autoComplete="off"
        maxLength={DEVICE_NAME_MAX_LENGTH}
        value={name}
        onChange={(event) => {
          setName(event.target.value);
          setErrors({});
        }}
        error={errors.name}
      />
      <div className={styles.actions}>
        <Button type="submit" loading={mutation.isPending}>
          Подключить
        </Button>
        {errors.form && (
          <p className={styles.formError} role="alert">
            {errors.form}
          </p>
        )}
        <ButtonLink to="/feed" variant="secondary">
          Назад
        </ButtonLink>
      </div>
    </form>
  );
}

function MissingToken() {
  return (
    <div className={styles.form}>
      <div className={styles.text}>
        <h2 className={styles.heading}>Ссылка неполная</h2>
        <p className={styles.description}>В ней нет кода привязки. Отсканируйте QR-код на экране E-va ещё раз.</p>
      </div>
      <div className={styles.actions}>
        <ButtonLink to="/claim/scan">Сканировать QR-код</ButtonLink>
        <ButtonLink to="/feed" variant="secondary">
          Назад
        </ButtonLink>
      </div>
    </div>
  );
}

function errorsFrom(error: Error): Errors {
  const byField = fieldErrors(error, ["name"] as const);
  return byField.name ? byField : { form: errorMessage(error) };
}
